import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { resolveFileType, type DocumentItem } from '@document-explorer/core';

/**
 * The same icon set the React package draws, as an Angular component.
 *
 * Core only ever names an `iconKey`; each renderer owns the actual markup,
 * which is what keeps the engine free of any DOM dependency.
 */
const PATHS: Record<string, string> = {
  folder: 'M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z',
  file: 'M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8zM14 3v5h5',
  pdf: 'M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8zM14 3v5h5M8.5 13.5h1.2a1 1 0 0 1 0 2H8.5v-2M12.5 13.5h1v2h-1M16 13.5h1.5',
  doc: 'M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8zM14 3v5h5M8.5 12.5h7M8.5 15.5h7M8.5 18h4',
  sheet: 'M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8zM14 3v5h5M8 12.5h8M8 16h8M12 12.5V19',
  slides: 'M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8zM14 3v5h5M8 12.5h8v5.5H8z',
  image: 'M5 4h14a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2zM7 10.5a1.5 1.5 0 1 0 3 0 1.5 1.5 0 0 0-3 0M3 16l4.5-4.5L14 18M14 14l2.5-2.5L21 16',
  video: 'M5 5h9a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2zM16 10l5-3v10l-5-3z',
  audio: 'M9 18V6l10-2v12M4 18a2.5 2.5 0 1 0 5 0 2.5 2.5 0 0 0-5 0M14 16a2.5 2.5 0 1 0 5 0 2.5 2.5 0 0 0-5 0',
  archive: 'M4 4h16a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1zM5 9v10a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V9M10 13h4',
  markdown: 'M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8zM14 3v5h5M8 17v-4l2 2 2-2v4M15.5 13v4M14 15.5l1.5 1.5 1.5-1.5',
  text: 'M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8zM14 3v5h5M8.5 12.5h7M8.5 15.5h7M8.5 18h4',
  code: 'M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8zM14 3v5h5M10 13l-2 2 2 2M14 13l2 2-2 2',
  data: 'M4 6c0-1.7 3.6-3 8-3s8 1.3 8 3-3.6 3-8 3-8-1.3-8-3zM4 6v12c0 1.7 3.6 3 8 3s8-1.3 8-3V6M4 12c0 1.7 3.6 3 8 3s8-1.3 8-3',
};

@Component({
  selector: 'de-icon',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <span class="de-icon" [style.color]="descriptor().color" [title]="descriptor().label">
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="1.75"
        stroke-linecap="round"
        stroke-linejoin="round"
        aria-hidden="true"
        focusable="false"
        width="100%"
        height="100%"
      >
        <path [attr.d]="path()" />
      </svg>
    </span>
  `,
})
export class DeIconComponent {
  readonly item = input<DocumentItem>({ id: '', name: '', type: 'file' });

  readonly descriptor = computed(() => resolveFileType(this.item()));
  readonly path = computed(() => PATHS[this.descriptor().iconKey] ?? PATHS['file']);
}
